import {Injectable} from '@angular/core';
import {PageRequest} from '../../shared/models/page-request.interface';
import {Observable, throwError} from 'rxjs';
import {Page} from '../../shared/models/page.interface';
import {environment} from '../../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {KFConfiguration} from '../models/kf-configuration.interface';
import {ApiHttpResponse} from '../../shared/models/api-http-response.interface';
import {catchError, map} from 'rxjs/operators';
import {Master} from '../../admin/models/master.interface';
import {KfConfigMappings} from '../models/kf-config-mapping.interface';
import {KfConfigurationSearch} from '../../kf-reporting/models/kf-configuration-search.interface';
import {ReportCycleSearch} from '../../admin/models/report-cycle-search.interface';
import {ClassifierMapping} from '../models/classifier-mapping.interface';

const KF_CONFIGURATION_API = environment.apiUrl + 'kfconfiguration/';
const UNU_KEYS_API = environment.apiUrl + 'UNU-keys';
const KF_CATEGORIES_API = environment.apiUrl + 'kf-categories';
const COLLECTION_STREAMS_API = environment.apiUrl + 'collection-streams';

@Injectable({
  providedIn: 'root'
})
export class KfConfigurationService {


  static getSortCode(sort: string): string {
    switch (sort) {
      default:
        return sort;
    }
  }

  static buildSearchParams(search: ReportCycleSearch): string {
    let searchParams = '';
    if (search.startYear && search.startYear !== '') {
      searchParams += `&startYear=${search.startYear}`;
    }
    if (search.endYear && search.endYear !== '') {
      searchParams += `&endYear=${search.endYear}`;
    }
    if (search.unuKeyName && search.unuKeyName !== '') {
      searchParams += `&unuKeyName=${search.unuKeyName}`;
    }
    if (search.kfCategoryName && search.kfCategoryName !== '') {
      searchParams += `&kfCategoryName=${search.kfCategoryName}`;
    }
    if (search.collectionStreamName && search.collectionStreamName !== '') {
      searchParams += `&collectionStreamName=${search.collectionStreamName}`;
    }
    if (search.launched || search.launched === false) {
      searchParams += `&launched=${search.launched}`;
    }
    if (search.startLaunched && search.startLaunched !== '') {
      searchParams += `&startLaunched=${search.startLaunched}`;
    }
    if (search.endLaunched && search.endLaunched !== '') {
      searchParams += `&endLaunched=${search.endLaunched}`;
    }
    if (search.open) {
      searchParams += `&open=${search.open}`;
    }
    return searchParams;
  }

  constructor(private http: HttpClient) {
  }

  getAllWeeeSystemKfConfiguration(pageRequest: PageRequest = {page: 0, size: 100}): Observable<Page<KFConfiguration>> {
    return this.getAllKfConfigurations(pageRequest, KF_CONFIGURATION_API + 'weeesystem');
  }

  getAllCountryKfConfiguration(pageRequest: PageRequest = {page: 0, size: 100}): Observable<Page<KFConfiguration>> {
    return this.getAllKfConfigurations(pageRequest, KF_CONFIGURATION_API + 'country');
  }

  getAllKfConfigurations(pageRequest: PageRequest, baseUrl: string): Observable<Page<KFConfiguration>> {
    let url = `${baseUrl}?size=${pageRequest.size}&page=${pageRequest.page}`;
    if (pageRequest.sort && pageRequest.direction) {
      url = url + `&sort=${KfConfigurationService.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<KFConfiguration>>(url);
  }

  searchKfConfigurations(search: KfConfigurationSearch, pageRequest: PageRequest, schemaKind: string): Observable<Page<KFConfiguration>> {
    let url = `${KF_CONFIGURATION_API}${schemaKind.toLowerCase()}?size=${pageRequest.size}&page=${pageRequest.page}`;
    const searchParams = KfConfigurationService.buildSearchParams(search);
    if (pageRequest.sort && pageRequest.direction) {
      url = url + `&sort=${KfConfigurationService.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<KFConfiguration>>(url + searchParams);
  }

  getByYear(year: string, schemaKind: string): Observable<ApiHttpResponse<KFConfiguration>> {
    return this.http.get<ApiHttpResponse<KFConfiguration>>(`${KF_CONFIGURATION_API}${schemaKind.toLowerCase()}/detail?year=${year}`);
  }

  saveKfConfiguration(kfConfig: KFConfiguration, schemaKind: string): Observable<ApiHttpResponse<KFConfiguration>> {
    return this.http.post<ApiHttpResponse<KFConfiguration>>(KF_CONFIGURATION_API + schemaKind.toLowerCase(), kfConfig).pipe(
      catchError(err => {
        return throwError(err);
      }),
      map((res: ApiHttpResponse<KFConfiguration>) => {
        return res;
      })
    );
  }

  getCategoriesByReferenceId(referenceId: string): Observable<Master[]> {
    return this.http.get<Master[]>(`${KF_CATEGORIES_API}/all?referenceId=${referenceId}`);
  }

  getCollectionStreamsByReferenceId(referenceId: string): Observable<Master[]> {
    return this.http.get<Master[]>(`${COLLECTION_STREAMS_API}/all?referenceId=${referenceId}`);
  }

  getKfConfigurationCategoriesMappings(referenceId: string, schemaKind: string): Observable<ApiHttpResponse<KfConfigMappings>> {
    return this.http.get<ApiHttpResponse<KfConfigMappings>>(`${KF_CONFIGURATION_API}${schemaKind.toLowerCase()}/kf-categories/unu-key-map?referenceId=${referenceId}`);
  }

  getKfConfigurationCollectionStreamsMappings(referenceId: string, schemaKind: string): Observable<ApiHttpResponse<KfConfigMappings>> {
    return this.http.get<ApiHttpResponse<KfConfigMappings>>
    (`${KF_CONFIGURATION_API}${schemaKind.toLowerCase()}/collection-streams/unu-key-map?referenceId=${referenceId}`);
  }

  saveKfConfigurationCategoriesMappings(kfConfigMappings: KfConfigMappings, schemaKind: string): Observable<any> {
    return this.saveUnuKeyMappings('kf-categories', kfConfigMappings, schemaKind.toLowerCase());
  }

  saveKfConfigurationCollectionStreamsMappings(kfConfigMappings: KfConfigMappings, schemaKind: string): Observable<any> {
    return this.saveUnuKeyMappings('collection-streams', kfConfigMappings, schemaKind);
  }

  private saveUnuKeyMappings(url: string, kfConfigMappings: KfConfigMappings, schemaKind: string): Observable<any> {
    return this.http.post<ApiHttpResponse<KFConfiguration>>
    (`${KF_CONFIGURATION_API}${schemaKind.toLowerCase()}/${url}/unu-key-map`, kfConfigMappings)
      .pipe(
        catchError(err => {
          return throwError(err);
        }),
        map((res: any) => {
          return res;
        })
      );
  }

  launchConfig(referenceId: string, schemaKind: string): Observable<any> {
    return this.http.get<ApiHttpResponse<KfConfigMappings>>
    (`${KF_CONFIGURATION_API}${schemaKind.toLowerCase()}/launch?referenceId=${referenceId}`);
  }

  reuseConfiguration(year: number, schemaKind: string): Observable<ApiHttpResponse<KFConfiguration>> {
    return this.http.get<ApiHttpResponse<KFConfiguration>>(`${KF_CONFIGURATION_API}${schemaKind.toLowerCase()}/reuse-country?year=${year}`);
  }

  reusePreviousConfiguration(year: number, yearToReuse: number, schemaKind: string): Observable<ApiHttpResponse<KFConfiguration>> {
    return this.http.get<ApiHttpResponse<KFConfiguration>>
    (`${KF_CONFIGURATION_API}${schemaKind.toLowerCase()}/reuse-previous?year=${year}&yearToReuse=${yearToReuse}`);
  }

  existConfigToReuse(year: number, schemaKind: string): Observable<boolean> {
    return this.http.get<boolean>(`${KF_CONFIGURATION_API}${schemaKind.toLowerCase()}/exists-country-config?year=${year}`);
  }

  getPreviousYears(schemaKind: string): Observable<ApiHttpResponse<number[]>> {
    return this.http.get<ApiHttpResponse<number[]>>
    (`${KF_CONFIGURATION_API}${schemaKind.toLowerCase()}/previous-years`);
  }

  getReportCycleUnuKeys(year: number, pageRequest: PageRequest = {page: 0, size: 100}): Observable<Page<Master>> {
    let url = `${UNU_KEYS_API}?year=${year}&size=${pageRequest.size}&page=${pageRequest.page}`;
    if (pageRequest.sort && pageRequest.direction) {
      url = url + `&sort=${KfConfigurationService.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<Master>>(url);
  }

  getReportCycleCategories(year: number, pageRequest: PageRequest = {page: 0, size: 100}): Observable<Page<Master>> {
    let url = `${KF_CATEGORIES_API}?year=${year}&size=${pageRequest.size}&page=${pageRequest.page}`;
    if (pageRequest.sort && pageRequest.direction) {
      url = url + `&sort=${KfConfigurationService.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<Master>>(url);
  }

  getReportCycleCollectionStreams(year: number, pageRequest: PageRequest = {page: 0, size: 100}): Observable<Page<Master>> {
    let url = `${COLLECTION_STREAMS_API}?year=${year}&size=${pageRequest.size}&page=${pageRequest.page}`;
    if (pageRequest.sort && pageRequest.direction) {
      url = url + `&sort=${KfConfigurationService.getSortCode(pageRequest.sort)},${pageRequest.direction}`;
    }
    return this.http.get<Page<Master>>(url);
  }

  getCategoriesSummaryMappings(showEmptyValues = true): Observable<ClassifierMapping[]> {
    return this.http.get<ClassifierMapping[]>(`${KF_CONFIGURATION_API}summary/kf-categories?showEmptyValues=${showEmptyValues}`);
  }

  getCollectionStreamsSummaryMappings(showEmptyValues = true): Observable<ClassifierMapping[]> {
    return this.http.get<ClassifierMapping[]>(`${KF_CONFIGURATION_API}summary/collection-streams?showEmptyValues=${showEmptyValues}`);
  }

  getCountryCodes(): Observable<string[]> {
    return this.http.get<string[]>(`${environment.apiUrl}countries/codes`);
  }

  getCategoriesSummaryMappingsExcel(showEmptyValues: boolean): Observable<any> {
    return this.http.get(`${KF_CONFIGURATION_API}summary/kf-categories/excel?showEmptyValues=${showEmptyValues}`, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  getCollectionStreamsSummaryMappingsExcel(showEmptyValues: boolean): Observable<any> {
    return this.http.get(`${KF_CONFIGURATION_API}summary/collection-streams/excel?showEmptyValues=${showEmptyValues}`, {
      observe: 'response',
      responseType: 'blob'
    });
  }


  existsKfRegistration(year: number, id: number, schemaKind: string): Observable<boolean> {
    return this.http.get<boolean>(`${KF_CONFIGURATION_API}${schemaKind.toLowerCase()}/exists-kf-registration?year=${year}&id=${id}`);
  }

  printPdf(year: number, schemaKind: string): Observable<any> {
    return this.http.get(`${KF_CONFIGURATION_API}${schemaKind.toLowerCase()}/detail/print?year=${year}`, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  checkMappings(referenceId: string, year: number, schemaKind: string): Observable<boolean> {
    return this.http.get<boolean>(`${KF_CONFIGURATION_API}${schemaKind.toLowerCase()}/check-mappings?referenceId=${referenceId}&year=${year}`);
  }

}
